/**
 * RBAC permission catalog — the single source of truth for permission strings.
 *
 * These strings are seeded into the `permissions` table by @pospak/db, enforced by the
 * API's `requirePermission()` middleware, and used by the web app to hide/disable
 * unauthorized UI. Never inline a permission string anywhere — always use `PERMISSIONS.*`.
 *
 * Naming: `group.action`, and — importantly for docs/SYNC.md §5 — read-only actions end in
 * `.view` and export actions end in `.export`, because the cloud read-only collapse filters
 * on exactly those suffixes.
 */
export const PERMISSION_GROUPS = [
    { key: 'catalog', label: 'Products & Categories' },
    { key: 'pricing', label: 'Pricing & Price Lists' },
    { key: 'inventory', label: 'Inventory & Stock' },
    { key: 'purchase', label: 'Purchases & Suppliers' },
    { key: 'sales', label: 'Selling & Bills' },
    { key: 'refunds', label: 'Refunds & Revisions' },
    { key: 'shift', label: 'Shifts' },
    { key: 'cash', label: 'Cash Management' },
    { key: 'customers', label: 'Customers & Credit' },
    { key: 'loyalty', label: 'Loyalty' },
    { key: 'orders', label: 'Online Orders' },
    { key: 'delivery', label: 'Delivery' },
    { key: 'accounts', label: 'Accounts & Ledgers' },
    { key: 'expenses', label: 'Expenses' },
    { key: 'reports', label: 'Reports' },
    { key: 'hr', label: 'Employees & Attendance' },
    { key: 'payroll', label: 'Payroll' },
    { key: 'mobile', label: 'Mobile Shop (Easyload & Wallets)' },
    { key: 'quotations', label: 'Quotations & Job Cards' },
    { key: 'fbr', label: 'FBR Digital Invoicing' },
    { key: 'barcode', label: 'Barcodes & Labels' },
    { key: 'settings', label: 'Settings' },
    { key: 'users', label: 'Users' },
    { key: 'roles', label: 'Roles & Permissions' },
    { key: 'shops', label: 'Shops' },
    { key: 'approvals', label: 'Approvals' },
    { key: 'audit', label: 'Audit' },
    { key: 'sync', label: 'Sync & Devices' },
    { key: 'support', label: 'Support' },
];
export const PERMISSIONS = {
    // ---- Catalog -----------------------------------------------------------
    CATALOG_VIEW: 'catalog.view',
    CATALOG_MANAGE: 'catalog.manage',
    CATALOG_DELETE: 'catalog.delete',
    CATALOG_IMPORT: 'catalog.import',
    CATALOG_EXPORT: 'catalog.export',
    // ---- Pricing -----------------------------------------------------------
    PRICING_VIEW: 'pricing.view',
    PRICING_MANAGE: 'pricing.manage',
    PRICING_COST_VIEW: 'pricing.cost.view',
    // ---- Inventory ---------------------------------------------------------
    INVENTORY_VIEW: 'inventory.view',
    INVENTORY_ADJUST: 'inventory.adjust',
    INVENTORY_ADJUST_APPROVE: 'inventory.adjust.approve',
    INVENTORY_TRANSFER: 'inventory.transfer',
    INVENTORY_TRANSFER_RECEIVE: 'inventory.transfer.receive',
    INVENTORY_COUNT: 'inventory.count',
    INVENTORY_COUNT_POST: 'inventory.count.post',
    INVENTORY_OPENING: 'inventory.opening',
    INVENTORY_EXPORT: 'inventory.export',
    // ---- Purchases ---------------------------------------------------------
    PURCHASE_VIEW: 'purchase.view',
    PURCHASE_ORDER_MANAGE: 'purchase.order.manage',
    PURCHASE_RECEIVE: 'purchase.receive',
    PURCHASE_RETURN: 'purchase.return',
    PURCHASE_PAY: 'purchase.pay',
    SUPPLIER_MANAGE: 'purchase.supplier.manage',
    PURCHASE_EXPORT: 'purchase.export',
    // ---- Sales -------------------------------------------------------------
    SALES_VIEW: 'sales.view',
    SALES_CREATE: 'sales.create',
    SALES_PARK: 'sales.park',
    SALES_VOID: 'sales.void',
    SALES_DISCOUNT: 'sales.discount',
    SALES_DISCOUNT_OVERRIDE: 'sales.discount.override',
    SALES_DISCOUNT_APPROVE: 'sales.discount.approve',
    SALES_PRICE_OVERRIDE: 'sales.price.override',
    SALES_CREDIT: 'sales.credit',
    SALES_PRINT: 'sales.print',
    SALES_REPRINT: 'sales.reprint',
    SALES_EXPORT: 'sales.export',
    /** Issue/list gift vouchers (`apps/api/src/modules/vouchers`). Verifying a
     * code and redeeming it as a bill payment only needs `SALES_CREATE` — this
     * one gates minting new value. */
    SALES_VOUCHER_MANAGE: 'sales.voucher.manage',
    // ---- Refunds & revisions ----------------------------------------------
    REFUNDS_VIEW: 'refunds.view',
    REFUNDS_CREATE: 'refunds.create',
    REFUNDS_APPROVE: 'refunds.approve',
    REFUNDS_WITHOUT_BILL: 'refunds.without.bill',
    BILLS_REVISE: 'refunds.bill.revise',
    BILLS_REVISE_APPROVE: 'refunds.bill.revise.approve',
    REFUNDS_EXPORT: 'refunds.export',
    // ---- Shifts ------------------------------------------------------------
    SHIFT_VIEW: 'shift.view',
    SHIFT_OPEN: 'shift.open',
    SHIFT_CLOSE: 'shift.close',
    SHIFT_CLOSE_FORCE: 'shift.close.force',
    SHIFT_CLOSE_OTHERS: 'shift.close.others',
    SHIFT_REOPEN: 'shift.reopen',
    SHIFT_EXPORT: 'shift.export',
    // ---- Cash --------------------------------------------------------------
    CASH_VIEW: 'cash.view',
    CASH_PAYIN: 'cash.payin',
    CASH_PAYOUT: 'cash.payout',
    CASH_DROP: 'cash.drop',
    CASH_DEPOSIT: 'cash.deposit',
    CASH_DAY_CLOSE: 'cash.day.close',
    CASH_VARIANCE_APPROVE: 'cash.variance.approve',
    CASH_EXPORT: 'cash.export',
    // ---- Customers ---------------------------------------------------------
    CUSTOMERS_VIEW: 'customers.view',
    CUSTOMERS_MANAGE: 'customers.manage',
    CUSTOMERS_MERGE: 'customers.merge',
    CUSTOMERS_CREDIT_MANAGE: 'customers.credit.manage',
    /** Issuing store credit onto a customer's account — the same "moves real
     * value" weight as `CUSTOMERS_CREDIT_MANAGE`, kept separate since a
     * business may trust a cashier with on-account CREDIT sales but not with
     * minting store-credit balance out of nothing. */
    CUSTOMERS_STORE_CREDIT_MANAGE: 'customers.storecredit.manage',
    CUSTOMERS_EXPORT: 'customers.export',
    // ---- Loyalty -----------------------------------------------------------
    LOYALTY_VIEW: 'loyalty.view',
    LOYALTY_REDEEM: 'loyalty.redeem',
    LOYALTY_ADJUST: 'loyalty.adjust',
    LOYALTY_CONFIGURE: 'loyalty.configure',
    LOYALTY_EXPORT: 'loyalty.export',
    // ---- Online orders -----------------------------------------------------
    ORDERS_VIEW: 'orders.view',
    ORDERS_ACCEPT: 'orders.accept',
    ORDERS_REJECT: 'orders.reject',
    ORDERS_EDIT: 'orders.edit',
    ORDERS_CANCEL_LATE: 'orders.cancel.late',
    ORDERS_DISPATCH: 'orders.dispatch',
    ORDERS_PORTAL_MANAGE: 'orders.portal.manage',
    ORDERS_EXPORT: 'orders.export',
    // ---- Delivery (README.md Phase 22) — drivers, runs, and the stop
    // outcomes that post COD and complete/reschedule each order. Separate
    // from `orders.dispatch` (the single-order jump straight to
    // OUT_FOR_DELIVERY that predates this group) since a run is a distinct
    // batch operation with its own driver-management concern. ------------
    DELIVERY_VIEW: 'delivery.view',
    /** Driver CRUD. */
    DELIVERY_MANAGE: 'delivery.manage',
    /** Create+dispatch a run, and complete one (post COD, reschedule failures). */
    DELIVERY_DISPATCH: 'delivery.dispatch',
    // ---- Accounts ----------------------------------------------------------
    ACCOUNTS_VIEW: 'accounts.view',
    ACCOUNTS_MANAGE: 'accounts.manage',
    ACCOUNTS_POST: 'accounts.post',
    ACCOUNTS_PERIOD_LOCK: 'accounts.period.lock',
    ACCOUNTS_EXPORT: 'accounts.export',
    // ---- Expenses ----------------------------------------------------------
    EXPENSES_VIEW: 'expenses.view',
    EXPENSES_CREATE: 'expenses.create',
    EXPENSES_APPROVE: 'expenses.approve',
    EXPENSES_PAY: 'expenses.pay',
    EXPENSES_EXPORT: 'expenses.export',
    // ---- Reports -----------------------------------------------------------
    REPORTS_SALES_VIEW: 'reports.sales.view',
    REPORTS_INVENTORY_VIEW: 'reports.inventory.view',
    REPORTS_TAX_VIEW: 'reports.tax.view',
    REPORTS_CASH_VIEW: 'reports.cash.view',
    REPORTS_ACCOUNTS_VIEW: 'reports.accounts.view',
    REPORTS_LOYALTY_VIEW: 'reports.loyalty.view',
    REPORTS_ORDERS_VIEW: 'reports.orders.view',
    REPORTS_MOBILE_VIEW: 'reports.mobile.view',
    REPORTS_HR_VIEW: 'reports.hr.view',
    /** Gates every cost/margin column, everywhere. */
    REPORTS_COST_VIEW: 'reports.cost.view',
    /** Cross-shop consolidated reporting. */
    REPORTS_ALL_SHOPS_VIEW: 'reports.allshops.view',
    REPORTS_EXPORT: 'reports.export',
    REPORTS_SCHEDULE_MANAGE: 'reports.schedule.manage',
    // ---- HR ----------------------------------------------------------------
    HR_VIEW: 'hr.view',
    HR_EMPLOYEE_MANAGE: 'hr.employee.manage',
    HR_ATTENDANCE_MANAGE: 'hr.attendance.manage',
    HR_LEAVE_APPROVE: 'hr.leave.approve',
    HR_EXPORT: 'hr.export',
    // ---- Payroll -----------------------------------------------------------
    PAYROLL_VIEW: 'payroll.view',
    PAYROLL_RUN: 'payroll.run',
    PAYROLL_APPROVE: 'payroll.approve',
    PAYROLL_PAY: 'payroll.pay',
    PAYROLL_EXPORT: 'payroll.export',
    // ---- Mobile shop -------------------------------------------------------
    MOBILE_VIEW: 'mobile.view',
    MOBILE_TXN_CREATE: 'mobile.txn.create',
    MOBILE_TXN_REVERSE: 'mobile.txn.reverse',
    MOBILE_ACCOUNT_MANAGE: 'mobile.account.manage',
    MOBILE_FLOAT_MANAGE: 'mobile.float.manage',
    MOBILE_DAY_CLOSE: 'mobile.day.close',
    MOBILE_MONTH_CLOSE: 'mobile.month.close',
    MOBILE_RECONCILE: 'mobile.reconcile',
    MOBILE_VARIANCE_APPROVE: 'mobile.variance.approve',
    MOBILE_EXPORT: 'mobile.export',
    // ---- Quotations / job cards (service businesses — vehicle decoration,
    // repair shops — quote a job, then convert the accepted quote straight
    // into a normal bill; docs/pospak-business-fit-audit.md's "weakest fit"
    // item) ---------------------------------------------------------------
    QUOTATIONS_VIEW: 'quotations.view',
    QUOTATIONS_MANAGE: 'quotations.manage',
    QUOTATIONS_CONVERT: 'quotations.convert',
    QUOTATIONS_EXPORT: 'quotations.export',
    // ---- FBR ---------------------------------------------------------------
    FBR_VIEW: 'fbr.view',
    FBR_CONFIGURE: 'fbr.configure',
    FBR_SUBMIT: 'fbr.submit',
    FBR_RETRY: 'fbr.retry',
    FBR_EXPORT: 'fbr.export',
    // ---- Barcode -----------------------------------------------------------
    BARCODE_VIEW: 'barcode.view',
    BARCODE_GENERATE: 'barcode.generate',
    BARCODE_ATTACH: 'barcode.attach',
    BARCODE_PRINT: 'barcode.print',
    BARCODE_TEMPLATE_MANAGE: 'barcode.template.manage',
    // ---- Settings ----------------------------------------------------------
    SETTINGS_VIEW: 'settings.view',
    SETTINGS_MANAGE: 'settings.manage',
    SETTINGS_SECRETS_MANAGE: 'settings.secrets.manage',
    // ---- Users / roles / shops --------------------------------------------
    USERS_VIEW: 'users.view',
    USERS_MANAGE: 'users.manage',
    USERS_RESET_PASSWORD: 'users.reset.password',
    ROLES_VIEW: 'roles.view',
    ROLES_MANAGE: 'roles.manage',
    SHOPS_VIEW: 'shops.view',
    SHOPS_MANAGE: 'shops.manage',
    // ---- Approvals / audit / sync -----------------------------------------
    APPROVALS_VIEW: 'approvals.view',
    APPROVALS_DECIDE: 'approvals.decide',
    AUDIT_VIEW: 'audit.view',
    AUDIT_EXPORT: 'audit.export',
    SYNC_VIEW: 'sync.view',
    SYNC_MANAGE: 'sync.manage',
    DEVICES_VIEW: 'sync.devices.view',
    DEVICES_MANAGE: 'sync.devices.manage',
    // ---- Support desk ------------------------------------------------------
    // `support.raise` is deliberately NOT `.view`-suffixed and is exempted from
    // the subscription-expiry collapse (middleware/rbac.ts): the business that
    // has just gone read-only is precisely the one that needs to tell somebody.
    SUPPORT_VIEW: 'support.view',
    SUPPORT_RAISE: 'support.raise',
};
/** Stable catalog order — used for seeding and for deterministic API output. */
export const PERMISSION_CODES = Object.values(PERMISSIONS);
const PERMISSION_SET = new Set(PERMISSION_CODES);
export function isPermission(value) {
    return PERMISSION_SET.has(value);
}
export function permissionGroup(code) {
    const head = code.split('.')[0];
    return head;
}
/**
 * Permissions that survive the cloud read-only collapse (docs/SYNC.md §5).
 * Anything ending `.view` or `.export` survives automatically; these are the
 * individually-reviewed write exceptions. The review question is always:
 * "does this touch a per-shop counter or stock?" If yes, it must NOT be here.
 */
export const CLOUD_WRITE_EXCEPTIONS = [
    PERMISSIONS.SETTINGS_MANAGE,
    PERMISSIONS.CATALOG_MANAGE,
    PERMISSIONS.PRICING_MANAGE,
    PERMISSIONS.LOYALTY_ADJUST,
];
/**
 * The symmetric collapse on a `local` deployment (README.md Phase 26 §3):
 * once the cloud is the only writer of catalog/pricing/settings (the reverse
 * sync channel pushes them down), a shop's own local staff must not ALSO be
 * able to write them — otherwise "cloud wins" is a fiction and a local edit
 * can race a reverse-pushed one into a silent lost update. Deliberately
 * `CLOUD_WRITE_EXCEPTIONS` MINUS `loyalty.adjust`: local writing loyalty is
 * the entire point of the offline-redemption path (README.md Phase 26 §4),
 * not something local ever gives up.
 */
export const LOCAL_WRITE_COLLAPSE = CLOUD_WRITE_EXCEPTIONS.filter((p) => p !== PERMISSIONS.LOYALTY_ADJUST);
/* -------------------------------------------------------------------------- */
/* System role templates (cloned per business at creation — docs/SPEC.md §2.1) */
/* -------------------------------------------------------------------------- */
export const SYSTEM_ROLES = {
    SUPER_ADMIN: 'SUPER_ADMIN',
    SHOP_MANAGER: 'SHOP_MANAGER',
    CASHIER: 'CASHIER',
    FRONT_DESK: 'FRONT_DESK',
    STOREKEEPER: 'STOREKEEPER',
    ACCOUNTANT: 'ACCOUNTANT',
    HR: 'HR',
    MOBILE_OPERATOR: 'MOBILE_OPERATOR',
    AUDITOR: 'AUDITOR',
};
const P = PERMISSIONS;
/** Every `.view`/`.export` permission — the AUDITOR's set. */
const READ_ONLY = PERMISSION_CODES.filter((c) => c.endsWith('.view') || c.endsWith('.export'));
export const SYSTEM_ROLE_PERMISSIONS = {
    [SYSTEM_ROLES.SUPER_ADMIN]: PERMISSION_CODES,
    [SYSTEM_ROLES.SHOP_MANAGER]: [
        P.SUPPORT_VIEW, P.SUPPORT_RAISE,
        P.CATALOG_VIEW, P.CATALOG_MANAGE, P.CATALOG_EXPORT,
        P.PRICING_VIEW, P.PRICING_MANAGE, P.PRICING_COST_VIEW,
        P.INVENTORY_VIEW, P.INVENTORY_ADJUST, P.INVENTORY_TRANSFER, P.INVENTORY_TRANSFER_RECEIVE,
        P.INVENTORY_COUNT, P.INVENTORY_COUNT_POST, P.INVENTORY_EXPORT,
        P.PURCHASE_VIEW, P.PURCHASE_ORDER_MANAGE, P.PURCHASE_RECEIVE, P.PURCHASE_RETURN,
        P.SUPPLIER_MANAGE, P.PURCHASE_EXPORT,
        P.SALES_VIEW, P.SALES_CREATE, P.SALES_PARK, P.SALES_VOID, P.SALES_DISCOUNT,
        P.SALES_DISCOUNT_OVERRIDE, P.SALES_DISCOUNT_APPROVE, P.SALES_PRICE_OVERRIDE,
        P.SALES_CREDIT, P.SALES_PRINT, P.SALES_REPRINT, P.SALES_EXPORT, P.SALES_VOUCHER_MANAGE,
        P.REFUNDS_VIEW, P.REFUNDS_CREATE, P.REFUNDS_APPROVE, P.BILLS_REVISE,
        P.BILLS_REVISE_APPROVE, P.REFUNDS_EXPORT,
        P.SHIFT_VIEW, P.SHIFT_OPEN, P.SHIFT_CLOSE, P.SHIFT_CLOSE_FORCE, P.SHIFT_CLOSE_OTHERS,
        P.SHIFT_EXPORT,
        P.CASH_VIEW, P.CASH_PAYIN, P.CASH_PAYOUT, P.CASH_DROP, P.CASH_DEPOSIT,
        P.CASH_DAY_CLOSE, P.CASH_VARIANCE_APPROVE, P.CASH_EXPORT,
        P.CUSTOMERS_VIEW, P.CUSTOMERS_MANAGE, P.CUSTOMERS_CREDIT_MANAGE, P.CUSTOMERS_STORE_CREDIT_MANAGE,
        P.LOYALTY_VIEW, P.LOYALTY_REDEEM,
        P.ORDERS_VIEW, P.ORDERS_ACCEPT, P.ORDERS_REJECT, P.ORDERS_EDIT, P.ORDERS_DISPATCH,
        P.ORDERS_PORTAL_MANAGE, P.ORDERS_EXPORT,
        P.DELIVERY_VIEW, P.DELIVERY_MANAGE, P.DELIVERY_DISPATCH,
        P.EXPENSES_VIEW, P.EXPENSES_CREATE,
        P.REPORTS_SALES_VIEW, P.REPORTS_INVENTORY_VIEW, P.REPORTS_TAX_VIEW,
        P.REPORTS_CASH_VIEW, P.REPORTS_LOYALTY_VIEW, P.REPORTS_ORDERS_VIEW,
        P.REPORTS_COST_VIEW, P.REPORTS_EXPORT,
        P.HR_VIEW, P.HR_ATTENDANCE_MANAGE,
        P.BARCODE_VIEW, P.BARCODE_GENERATE, P.BARCODE_ATTACH, P.BARCODE_PRINT,
        P.QUOTATIONS_VIEW, P.QUOTATIONS_MANAGE, P.QUOTATIONS_CONVERT, P.QUOTATIONS_EXPORT,
        P.SETTINGS_VIEW,
        P.USERS_VIEW,
        P.APPROVALS_VIEW, P.APPROVALS_DECIDE,
    ],
    [SYSTEM_ROLES.CASHIER]: [
        P.CATALOG_VIEW,
        P.PRICING_VIEW,
        P.INVENTORY_VIEW,
        P.SALES_VIEW, P.SALES_CREATE, P.SALES_PARK, P.SALES_DISCOUNT, P.SALES_PRINT,
        P.REFUNDS_VIEW, P.REFUNDS_CREATE,
        P.SHIFT_VIEW, P.SHIFT_OPEN, P.SHIFT_CLOSE,
        P.CASH_VIEW,
        P.CUSTOMERS_VIEW, P.CUSTOMERS_MANAGE,
        P.LOYALTY_VIEW, P.LOYALTY_REDEEM,
        P.ORDERS_VIEW,
        P.BARCODE_VIEW,
        // Not QUOTATIONS_CONVERT: converting bills a quote's exact snapshot
        // price straight through, bypassing the normal SALES_PRICE_OVERRIDE
        // check every other price change requires — that sign-off stays with
        // whoever can already override a price (SHOP_MANAGER).
        P.QUOTATIONS_VIEW, P.QUOTATIONS_MANAGE,
    ],
    [SYSTEM_ROLES.FRONT_DESK]: [
        P.CATALOG_VIEW,
        P.PRICING_VIEW,
        P.INVENTORY_VIEW,
        P.SALES_VIEW, P.SALES_CREATE, P.SALES_PARK, P.SALES_PRINT,
        P.REFUNDS_VIEW,
        P.BILLS_REVISE,
        // Front desk takes cash across the counter (SALES_CREATE above), so it
        // needs its own till — without these it deadlocked on the first sale of
        // the day: `shift.blockSalesWithoutShift` defaults ON, so selling refuses
        // with SHIFT_REQUIRED while opening a shift refused with FORBIDDEN, and
        // the only way out was to fetch someone with a bigger role. Same four a
        // CASHIER has, and no more: closing OTHER people's shifts
        // (SHIFT_CLOSE_OTHERS) and moving money in and out of the drawer
        // (CASH_PAYIN/PAYOUT/DROP) stay with the manager.
        P.SHIFT_VIEW, P.SHIFT_OPEN, P.SHIFT_CLOSE,
        P.CASH_VIEW,
        P.CUSTOMERS_VIEW, P.CUSTOMERS_MANAGE,
        P.LOYALTY_VIEW,
        P.ORDERS_VIEW, P.ORDERS_ACCEPT, P.ORDERS_REJECT, P.ORDERS_EDIT, P.ORDERS_DISPATCH,
        P.DELIVERY_VIEW, P.DELIVERY_DISPATCH,
        P.QUOTATIONS_VIEW, P.QUOTATIONS_MANAGE,
    ],
    [SYSTEM_ROLES.STOREKEEPER]: [
        P.CATALOG_VIEW, P.CATALOG_MANAGE, P.CATALOG_IMPORT, P.CATALOG_EXPORT,
        P.PRICING_VIEW, P.PRICING_COST_VIEW,
        P.INVENTORY_VIEW, P.INVENTORY_ADJUST, P.INVENTORY_TRANSFER,
        P.INVENTORY_TRANSFER_RECEIVE, P.INVENTORY_COUNT, P.INVENTORY_EXPORT,
        P.PURCHASE_VIEW, P.PURCHASE_ORDER_MANAGE, P.PURCHASE_RECEIVE, P.PURCHASE_RETURN,
        P.SUPPLIER_MANAGE, P.PURCHASE_EXPORT,
        P.REPORTS_INVENTORY_VIEW, P.REPORTS_EXPORT,
        P.BARCODE_VIEW, P.BARCODE_GENERATE, P.BARCODE_ATTACH, P.BARCODE_PRINT,
    ],
    [SYSTEM_ROLES.ACCOUNTANT]: [
        P.CATALOG_VIEW, P.PRICING_VIEW, P.PRICING_COST_VIEW, P.INVENTORY_VIEW,
        P.PURCHASE_VIEW, P.PURCHASE_PAY, P.PURCHASE_EXPORT,
        P.SALES_VIEW, P.SALES_EXPORT,
        P.REFUNDS_VIEW, P.REFUNDS_EXPORT,
        P.SHIFT_VIEW, P.SHIFT_EXPORT,
        P.CASH_VIEW, P.CASH_DEPOSIT, P.CASH_EXPORT,
        P.CUSTOMERS_VIEW, P.CUSTOMERS_CREDIT_MANAGE, P.CUSTOMERS_EXPORT,
        P.ACCOUNTS_VIEW, P.ACCOUNTS_MANAGE, P.ACCOUNTS_POST, P.ACCOUNTS_PERIOD_LOCK,
        P.ACCOUNTS_EXPORT,
        P.EXPENSES_VIEW, P.EXPENSES_CREATE, P.EXPENSES_APPROVE, P.EXPENSES_PAY,
        P.EXPENSES_EXPORT,
        P.REPORTS_SALES_VIEW, P.REPORTS_TAX_VIEW, P.REPORTS_CASH_VIEW,
        P.REPORTS_ACCOUNTS_VIEW, P.REPORTS_INVENTORY_VIEW, P.REPORTS_COST_VIEW,
        P.REPORTS_ALL_SHOPS_VIEW, P.REPORTS_EXPORT,
        P.FBR_VIEW, P.FBR_EXPORT,
        P.MOBILE_VIEW, P.MOBILE_EXPORT, P.MOBILE_MONTH_CLOSE, P.MOBILE_RECONCILE,
        P.MOBILE_VARIANCE_APPROVE,
    ],
    [SYSTEM_ROLES.HR]: [
        P.HR_VIEW, P.HR_EMPLOYEE_MANAGE, P.HR_ATTENDANCE_MANAGE, P.HR_LEAVE_APPROVE,
        P.HR_EXPORT,
        P.PAYROLL_VIEW, P.PAYROLL_RUN, P.PAYROLL_EXPORT,
        P.REPORTS_HR_VIEW, P.REPORTS_EXPORT,
        P.USERS_VIEW,
    ],
    [SYSTEM_ROLES.MOBILE_OPERATOR]: [
        P.MOBILE_VIEW, P.MOBILE_TXN_CREATE, P.MOBILE_DAY_CLOSE, P.MOBILE_EXPORT,
        P.CUSTOMERS_VIEW,
        P.SHIFT_VIEW, P.SHIFT_OPEN, P.SHIFT_CLOSE,
        P.CASH_VIEW,
        P.REPORTS_MOBILE_VIEW,
    ],
    [SYSTEM_ROLES.AUDITOR]: READ_ONLY,
};
//# sourceMappingURL=permissions.js.map